import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { LayoutDashboard, FolderPlus, Calculator, LogOut } from 'lucide-react';
import { useAuthStore } from '../stores/auth';

export function BusinessLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const handleSignOut = async () => {
    await signOut();
    // Prevent the next user on this browser from briefly seeing cached data
    // from the account we just logged out of.
    queryClient.clear();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50" dir="rtl">
      {/* Top Nav */}
      <header className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/business" className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-business-primary flex items-center justify-center">
                <span className="text-white font-bold">F</span>
              </div>
              <div>
                <div className="font-semibold text-navy">FAI360 Business</div>
                <div className="text-xs text-slate-500">אשראי עסקי ליועצים</div>
              </div>
            </Link>

            <nav className="hidden md:flex items-center gap-1">
              <NavLink to="/business" active={location.pathname === '/business'} icon={LayoutDashboard}>
                לוח בקרה
              </NavLink>
              <NavLink to="/business/cases/new" active={isActive('/business/cases/new')} icon={FolderPlus}>
                קייס חדש
              </NavLink>
              <NavLink to="/business/calculator" active={isActive('/business/calculator')} icon={Calculator}>
                מחשבון מהיר
              </NavLink>
            </nav>
          </div>

          <div className="flex items-center gap-3">
            {user?.email && (
              <div className="text-sm text-slate-600 hidden sm:block" dir="ltr">
                {user.email}
              </div>
            )}
            <button
              type="button"
              onClick={handleSignOut}
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition"
            >
              <LogOut className="w-4 h-4" />
              יציאה
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="container mx-auto px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}

function NavLink({
  to,
  active,
  icon: Icon,
  children,
}: {
  to: string;
  active: boolean;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
        active
          ? 'bg-business-primary/10 text-business-primary'
          : 'text-slate-600 hover:bg-slate-100'
      }`}
    >
      <Icon className="w-4 h-4" />
      {children}
    </Link>
  );
}
